import React, { useEffect } from "react";
import { CommonOutletTemplate } from "UI/CommonOutletTemplate";
import { DataGrid } from "UI/DataGrid";
import { AdminDashboardGreetingsDialog } from "./AdminDashboardGreetingsDialog";
import { workingColumns, workingMobileColumns } from "./Referrals.data";
import {
  notificationsColumns,
  notificationsMobileColumns,
} from "./Notifications.data";
import { questionsColumns, questionsMobileColumns } from "./Questions.data";
import {
  superAdminReferralsColumns,
  superAdminReferralsMobileColumns,
} from "./SuperAdminReferrals.data";
import {
  superAdminQuestionsColumns,
  superAdminQuestionsMobileColumns,
} from "./SuperAdminQuestions.data";
import {
  superAdminNotificationsColumns,
  superAdminNotificationsMobileColumns,
} from "./SuperAdminNotifications.data";
import { archiveColumns, archiveMobileColumns } from "./ReferralsArchive.data";
import { useReferrals } from "./Referrals.hook";
import { Box, Stack, Tab, Tabs } from "@mui/material";
import { useTabs } from "hooks/useTabs";
import { TabPanel } from "./TabPanel";
import { useIndicators } from "hooks/useIndicators";
import { useRef } from "react";
import NewIndicator from "./NewIndicator";
import {
  superAdminArchiveColumns,
  superAdminArchiveMobileColumns,
} from "./SuperAdminReferralsArchive.data";
import { LocationsDescription } from "./LocationsDescription";

export const Referrals = () => {
  const { currentTab, handleChangeTab } = useTabs({
    key: "ReferralStream",
  });

  const { notifications, questions, referrals, reloadIndicators } =
    useIndicators();
  const firstLoad = useRef(true);

  const {
    isGreetingsRead,
    handleCloseGreetings,
    handleRowClick,
    notificationDataGrid,
    questionDataGrid,
    referralDataGrid,
    archiveDataGrid,
    disableDownload,
    isSuperAdmin,
  } = useReferrals({
    onRefresh: reloadIndicators,
  });

  useEffect(() => {
    if (firstLoad.current) {
      firstLoad.current = false;
      return;
    }

    switch (currentTab) {
      case 0:
        notificationDataGrid?.loadData();
        break;
      case 1:
        questionDataGrid?.loadData();
        break;
      case 2:
        referralDataGrid?.loadData();
        break;
      case 3:
        archiveDataGrid?.loadData();
        break;
      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab]);

  return (
    <CommonOutletTemplate title="Referral Stream">
      <Box sx={{ flex: 0, borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={currentTab}
          onChange={handleChangeTab}
          aria-label="basic tabs example"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab
            label={
              <Stack direction="row" alignItems={"center"} gap={0.5}>
                Notifications
                <NewIndicator count={notifications} />
              </Stack>
            }
          />
          <Tab
            label={
              <Stack direction="row" alignItems={"center"} gap={0.5}>
                Questions <NewIndicator count={questions} />
              </Stack>
            }
          />
          <Tab
            label={
              <Stack direction="row" alignItems={"center"} gap={0.5}>
                Referrals <NewIndicator count={referrals} />
              </Stack>
            }
          />
          <Tab label="Archive" />
        </Tabs>
      </Box>
      <LocationsDescription />
      <div className="flex-1 flex flex-col justify-between items-stretch mt-2 overflow-hidden">
        <TabPanel value={currentTab} index={0}>
          <DataGrid
            columns={
              isSuperAdmin
                ? superAdminNotificationsColumns
                : notificationsColumns
            }
            mobileColumns={
              isSuperAdmin
                ? superAdminNotificationsMobileColumns
                : notificationsMobileColumns
            }
            handleRowClick={handleRowClick}
            {...notificationDataGrid}
            withRefreshButton
            disableDownload={disableDownload}
          />
        </TabPanel>
        <TabPanel value={currentTab} index={1}>
          <DataGrid
            columns={
              isSuperAdmin ? superAdminQuestionsColumns : questionsColumns
            }
            mobileColumns={
              isSuperAdmin
                ? superAdminQuestionsMobileColumns
                : questionsMobileColumns
            }
            handleRowClick={handleRowClick}
            {...questionDataGrid}
            withRefreshButton
            disableDownload={disableDownload}
          />
        </TabPanel>
        <TabPanel value={currentTab} index={2}>
          <DataGrid
            columns={isSuperAdmin ? superAdminReferralsColumns : workingColumns}
            mobileColumns={
              isSuperAdmin
                ? superAdminReferralsMobileColumns
                : workingMobileColumns
            }
            handleRowClick={handleRowClick}
            {...referralDataGrid}
            withRefreshButton
            disableDownload={disableDownload}
          />
        </TabPanel>
        <TabPanel value={currentTab} index={3}>
          <DataGrid
            columns={isSuperAdmin ? superAdminArchiveColumns : archiveColumns}
            mobileColumns={
              isSuperAdmin
                ? superAdminArchiveMobileColumns
                : archiveMobileColumns
            }
            handleRowClick={handleRowClick}
            {...archiveDataGrid}
            withRefreshButton
            disableDownload={disableDownload}
          />
        </TabPanel>
      </div>
      <AdminDashboardGreetingsDialog
        open={!isGreetingsRead}
        handleClose={handleCloseGreetings}
      />
    </CommonOutletTemplate>
  );
};
